import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { orgType } from 'src/app/config/app-constants';
import { SiteMoveHistoryItem, SiteMoveService } from 'src/app/internal-user/customer-management/services/site-move.service';
import { Role } from '../../models/auth/role';

@Component({
  selector: 'app-site-move-history-dialog',
  templateUrl: './site-move-history-dialog.component.html',
  styleUrls: ['./site-move-history-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatDialogModule, MatIconModule],
})
export class SiteMoveHistoryDialogComponent implements OnInit, OnDestroy {
  readonly pageSize = 25;
  items: SiteMoveHistoryItem[] = [];
  totalCount = 0;
  page = 1;
  loading = false;
  loaded = false;
  denied = false;
  error = '';
  expandedOperationId: string | undefined;
  profileChanged = false;
  private initialized = false;
  private eligible = false;
  private request: Subscription | undefined;
  private readonly destroyed = new Subject<void>();

  constructor(
    private readonly dialogRef: MatDialogRef<SiteMoveHistoryDialogComponent>,
    private readonly auth: AuthService,
    private readonly siteMoves: SiteMoveService,
  ) {}

  ngOnInit(): void {
    this.auth.profile$.pipe(takeUntil(this.destroyed)).subscribe(profile => {
      this.request?.unsubscribe();
      this.clearHistory();
      if (this.initialized) {
        this.profileChanged = true;
        this.eligible = false;
        this.denied = true;
        this.error = 'Your profile changed. Close and reopen Site Move History to check access again.';
        return;
      }
      this.initialized = true;
      this.eligible = this.auth.loggedIn && profile?.orgType !== orgType.EXTERNAL
        && profile?.hasAnyRoleOf(Role.admin, Role.parataDev);
      if (this.eligible) {
        this.loadPage(1);
      } else {
        this.denied = true;
        this.error = 'You do not have access to Site Move History.';
      }
    });
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  get firstItem(): number {
    return this.items.length ? (this.page - 1) * this.pageSize + 1 : 0;
  }

  get lastItem(): number {
    return this.items.length ? this.firstItem + this.items.length - 1 : 0;
  }

  refresh(): void {
    this.loadPage(1);
  }

  goToPage(page: number): void {
    if (!this.loading && page >= 1 && page <= this.pageCount && page !== this.page) {
      this.loadPage(page);
    }
  }

  toggleDetails(operationId: string): void {
    this.expandedOperationId = this.expandedOperationId === operationId ? undefined : operationId;
  }

  close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.eligible = false;
    this.request?.unsubscribe();
    this.destroyed.next();
    this.destroyed.complete();
    this.clearHistory();
  }

  private loadPage(page: number): void {
    if (!this.eligible || this.profileChanged) { return; }
    this.request?.unsubscribe();
    this.clearHistory();
    this.page = page;
    this.denied = false;
    this.error = '';
    this.loading = true;
    // The history endpoint rechecks live authorization; menu visibility is not permission.
    this.request = this.siteMoves.history(page, this.pageSize).subscribe({
      next: history => {
        this.items = history.items;
        this.totalCount = history.totalCount;
        this.page = history.page;
        this.loaded = true;
        this.loading = false;
      },
      error: response => {
        this.loading = false;
        this.denied = response?.status === 401 || response?.status === 403;
        this.error = this.denied
          ? 'You do not have access to Site Move History. Your permission may have changed.'
          : 'Site Move History could not be loaded. Refresh to try loading history again.';
      },
    });
  }

  private clearHistory(): void {
    this.items = [];
    this.totalCount = 0;
    this.expandedOperationId = undefined;
    this.loaded = false;
    this.loading = false;
  }
}
